import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import message from 'antd/lib/message'

import PrimaryButton from 'components/PrimaryButton'
import UikFormInputGroup from 'components/UikFormInputGroup'
import UikHeadline from 'components/UikHeadline'
import UikHeadlineDesc from 'components/UikHeadlineDesc'
import UikInput from 'components/UikInput'
import UikWidget from 'components/UikWidget'
import UikWidgetContent from 'components/UikWidgetContent'

import useAuthState from 'hooks/useAuthState'
import useLogin from 'hooks/useLogin'
import { analytics, apiManager, constants } from 'helpers'
import Auth from 'layouts/Auth'
import cls from '../Auth.module.scss'
import rightBackgroundImg from 'assets/images/login.png'

const validateEmail = email => {
  const regexp =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return regexp.test(email)
}

const {
  ROUTES: { RESET_PASSWORD, LANDING, HOST_PORTAL },
} = constants

const Login = ({ match, history, location }) => {
  const [userEmailIsNotValid, updateUserEmailIsNotValid] = useState()
  const [userPasswordIsNotValid, updateUserPasswordIsNotValid] = useState()
  const [loginBtnIsLoading, setLoginBtnAsLoading] = useState(false)
  const [userIsLoggingIn, setUserIsLoggingIn] = useState(false)

  const { user } = useAuthState()
  const login = useLogin()

  const redirectAfterLogin = useCallback(
    user => {
      history.push(user.Host ? HOST_PORTAL : LANDING)
    },
    [history]
  )

  const doLogin = useCallback(
    async user => {
      setLoginBtnAsLoading(true)
      localStorage.clear()
      try {
        const data = await apiManager.login(user)
        await login(data)
        setUserIsLoggingIn(true)
      } catch (err) {
        console.error(err)
        setLoginBtnAsLoading(false)
      }
    },
    [login]
  )

  const onObtainUserAfterLogin = useCallback(async () => {
    localStorage.email = user.email

    analytics.segmentTrack('Host Logs In')

    const eventId = match?.params?.event_id

    if (eventId) {
      try {
        await apiManager.inviteUserToEvent({ eventId })
        analytics.segmentTrack('User Logged In And Was Invited To Event', {
          eventId,
        })
        history.push(`/events/${eventId}`)
      } catch (err) {
        console.error(err)
        console.error(`Error inviting user ${user.id} to ${eventId} event`, err)
        history.push(LANDING)
      }
    } else {
      redirectAfterLogin(user)
    }
  }, [history, match, user, redirectAfterLogin])

  useEffect(() => {
    const { search } = location
    if (search) {
      const magicToken = new URLSearchParams(search).get('magicToken')
      if (magicToken) {
        doLogin({ magicToken, authMethod: 'MagicToken' })
      }
    }
  }, [location, doLogin])

  useEffect(() => {
    if (user && user.id && userIsLoggingIn) {
      onObtainUserAfterLogin()
    } else if (user && user.id) {
      message.loading('Logging you in', 0.3).then(() => {
        redirectAfterLogin(user)
      })
    }
  }, [user, userIsLoggingIn, redirectAfterLogin, onObtainUserAfterLogin])

  // State for the user info
  const [userLoginData, updateUserLoginData] = useState({
    email: '',
    password: '',
    authMethod: 'Password',
  })

  const userInputIsAllValid = userData => {
    if (!userData.email) {
      updateUserEmailIsNotValid('Email cannot be empty')
      setLoginBtnAsLoading(false)
      return false
    }

    if (!validateEmail(userData.email)) {
      updateUserEmailIsNotValid('Email is invalid')
      setLoginBtnAsLoading(false)
      return false
    }

    if (!userData.password) {
      updateUserPasswordIsNotValid('Password cannot be empty')
      setLoginBtnAsLoading(false)
      return false
    }
    return true
  }

  const loginWithEmail = () => {
    updateUserEmailIsNotValid()
    updateUserPasswordIsNotValid()
    if (userInputIsAllValid(userLoginData)) {
      doLogin(userLoginData)
    }
  }

  const submitOnEnter = event => {
    // 'keypress' event misbehaves on mobile so we track 'Enter' key via 'keydown' event
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      loginWithEmail()
    }
  }

  return (
    <Auth>
      <UikWidget className={cls.widgetWrapper}>
        <div className={cls.content}>
          <UikWidgetContent className={cls.left}>
            <UikHeadline>Host Sign In</UikHeadline>
            <UikHeadlineDesc>Enter your account information to sign in to Teleport</UikHeadlineDesc>
            <UikFormInputGroup>
              <UikInput
                label="Email"
                name="email"
                type="email"
                errorMessage={userEmailIsNotValid}
                onChange={e => {
                  const emailVal = e.target.value
                  updateUserLoginData({
                    ...userLoginData,
                    email: emailVal,
                  })
                }}
                onKeyDown={submitOnEnter}
              />
              <UikInput
                label="Password"
                name="password"
                placeholder=""
                type="password"
                errorMessage={userPasswordIsNotValid}
                onChange={e => {
                  const passVal = e.target.value
                  updateUserLoginData({
                    ...userLoginData,
                    password: passVal,
                  })
                }}
                onKeyDown={submitOnEnter}
              />
            </UikFormInputGroup>
            <p>
              {' '}
              <Link
                onClick={() => {
                  analytics.segmentTrack('User Clicked Forgot Password Button')
                }}
                to={RESET_PASSWORD}
              >
                Forgot password?
              </Link>
            </p>
            <br />
            <PrimaryButton
              className="w-full text-base"
              loading={loginBtnIsLoading}
              onClick={loginWithEmail}
            >
              SIGN IN
            </PrimaryButton>
          </UikWidgetContent>
          <div
            className={cls.right}
            style={{
              backgroundImage: `linear-gradient(-180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.25) 100%), url(${rightBackgroundImg})`,
            }}
          />
        </div>
      </UikWidget>
    </Auth>
  )
}

export default Login
